/**
 * User Factory
 * Handles creation and instantiation of User objects
 */

import { User, UserData } from './User';
import { BaseRole } from './roles/BaseRole';
import { MerchantRole } from './roles/MerchantRole';
import { AdminRole } from './roles/AdminRole';
import { CustomerRole } from './roles/CustomerRole';

export class UserFactory {
  /**
   * Create a User from API response data
   */
  static fromApiResponse(data: any): User {
    // Map API response to UserData format
    const userData: UserData = {
      _id: data._id || data.id || '',
      username: data.username || data.email || '',
      email: data.email,
      profile: {
        firstName: data.profile?.firstName || data.firstName,
        lastName: data.profile?.lastName || data.lastName,
        phone: data.profile?.phone || data.phone,
        email: data.profile?.email || data.email,
        avatar: data.profile?.avatar || data.avatar,
        bio: data.profile?.bio,
        dateOfBirth: data.profile?.dateOfBirth,
        address: data.profile?.address || data.address
      },
      roles: data.roles || [],
      balances: data.balances || {},
      otherProfiles: data.otherProfiles || {},
      hasAdminAccess: data.hasAdminAccess || false,
      isCustomerSupport: data.isCustomerSupport || false,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
      lastLogin: data.lastLogin,
      isGuest: data.isGuest || false
    };
    
    return new User(userData);
  }

  /**
   * Create a guest user
   */
  static createGuest(username?: string): User {
    const guestUsername = username || `guest_${Date.now()}`;
    return User.createGuest(guestUsername);
  }

  /**
   * Create an empty/null user
   */
  static createEmpty(): User {
    return User.createEmpty();
  }

  /**
   * Get the primary role for a user
   */
  static getPrimaryRole(user: User): BaseRole {
    // Priority order: Admin > Merchant > Customer
    if (user.isAdmin || user.isSupport) {
      return new AdminRole(user);
    }
    
    if (user.isMerchant) {
      return new MerchantRole(user);
    }
    
    return new CustomerRole(user);
  }

  /**
   * Get all applicable roles for a user
   */
  static getAllRoles(user: User): BaseRole[] {
    const roles: BaseRole[] = [];
    
    if (user.isAdmin || user.isSupport) {
      roles.push(new AdminRole(user));
    }
    
    if (user.isMerchant) {
      roles.push(new MerchantRole(user));
    }
    
    // Everyone has customer role capabilities
    roles.push(new CustomerRole(user));
    
    return roles;
  }

  /**
   * Create a user with specific role
   */
  static createWithRole(
    userData: Partial<UserData>,
    role: 'admin' | 'merchant' | 'customer' | 'support'
  ): User {
    const baseData: UserData = {
      _id: userData._id || `user_${Date.now()}`,
      username: userData.username || '',
      email: userData.email,
      profile: userData.profile || {},
      roles: [],
      balances: userData.balances || {},
      otherProfiles: userData.otherProfiles || {},
      hasAdminAccess: false,
      isCustomerSupport: false,
      isGuest: false
    };
    
    switch (role) {
      case 'admin':
        baseData.roles = ['ADMIN', 'USER'];
        baseData.hasAdminAccess = true;
        break;
        
      case 'support':
        baseData.roles = ['SUPPORT', 'USER'];
        baseData.isCustomerSupport = true;
        break;
        
      case 'merchant':
        baseData.roles = ['MERCHANT', 'USER'];
        baseData.otherProfiles = {
          ...baseData.otherProfiles,
          merchant: userData.otherProfiles?.merchant || {
            businessName: '',
            businessType: 'retail',
            isPublished: false,
            completedOnboarding: false
          }
        };
        break;
        
      case 'customer':
      default:
        baseData.roles = ['USER'];
        break;
    }
    
    return new User(baseData);
  }

  /**
   * Clone a user with updates
   */
  static cloneWithUpdates(user: User, updates: Partial<UserData>): User {
    const currentData = user.toJSON();
    const newData = {
      ...currentData,
      ...updates,
      profile: {
        ...currentData.profile,
        ...(updates.profile || {})
      },
      otherProfiles: {
        ...currentData.otherProfiles,
        ...(updates.otherProfiles || {})
      }
    };
    
    return new User(newData);
  }

  /**
   * Validate user data before creation
   */
  static validate(data: any): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!data._id && !data.id) {
      errors.push('User ID is required');
    }
    
    if (!data.username && !data.email) {
      errors.push('Username or email is required');
    }
    
    if (data.roles && !Array.isArray(data.roles)) {
      errors.push('Roles must be an array');
    }
    
    if (data.balances && typeof data.balances !== 'object') {
      errors.push('Balances must be an object');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Merge user data from multiple sources
   */
  static mergeUserData(...sources: Partial<UserData>[]): UserData {
    const merged: UserData = {
      _id: '',
      username: '',
      balances: {}
    };
    
    for (const source of sources) {
      if (source._id) merged._id = source._id;
      if (source.username) merged.username = source.username;
      if (source.email) merged.email = source.email;
      
      if (source.profile) {
        merged.profile = {
          ...merged.profile,
          ...source.profile
        };
      }
      
      if (source.roles) {
        merged.roles = [...new Set([...(merged.roles || []), ...source.roles])];
      }
      
      if (source.balances) {
        merged.balances = {
          ...merged.balances,
          ...source.balances
        };
      }
      
      if (source.otherProfiles) {
        merged.otherProfiles = {
          ...merged.otherProfiles,
          ...source.otherProfiles
        };
      }
      
      if (source.hasAdminAccess) merged.hasAdminAccess = source.hasAdminAccess;
      if (source.isCustomerSupport) merged.isCustomerSupport = source.isCustomerSupport;
      if (source.createdAt) merged.createdAt = source.createdAt;
      if (source.updatedAt) merged.updatedAt = source.updatedAt;
      if (source.lastLogin) merged.lastLogin = source.lastLogin;
      if (source.isGuest !== undefined) merged.isGuest = source.isGuest;
    }
    
    return merged;
  }
}
